import { Checkbox, type CheckboxProps } from "../checkbox/checkbox";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "../drawer/drawer";
import { Field, FieldContent, FieldLabel } from "../field/field";
import { Icon } from "../icons/icon";
import { PortalContainerProvider } from "../portal-container/portal-container";
import { useIsMobile } from "../../hooks/use-mobile";
import { cn } from "../../lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { Dialog as DialogPrimitive } from "radix-ui";

type DialogMobileMode = "drawer" | "dialog";
type DialogRenderMode = "dialog" | "drawer";

type DialogContextValue = {
  renderMode: DialogRenderMode;
};

const DialogContext = React.createContext<DialogContextValue | null>(null);

function useDialogRenderMode() {
  return React.useContext(DialogContext)?.renderMode ?? "dialog";
}

function shouldRenderDrawer(mobileMode: DialogMobileMode, isMobile: boolean) {
  return mobileMode === "drawer" && (isMobile || isMobileViewport());
}

function isMobileViewport() {
  return typeof window !== "undefined" && window.innerWidth < 768;
}

type DialogProps = Omit<
  React.ComponentProps<typeof DialogPrimitive.Root>,
  "open" | "defaultOpen" | "onOpenChange"
> & {
  defaultOpen?: boolean;
  mobileMode?: DialogMobileMode;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
};

function Dialog({
  children,
  defaultOpen = false,
  mobileMode = "drawer",
  modal,
  onOpenChange,
  open: controlledOpen,
}: DialogProps) {
  const isMobile = useIsMobile();
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const [lockedMode, setLockedMode] = React.useState<DialogRenderMode | null>(
    null,
  );
  const open = controlledOpen ?? uncontrolledOpen;
  const resolvedMode: DialogRenderMode = shouldRenderDrawer(
    mobileMode,
    isMobile,
  )
    ? "drawer"
    : "dialog";
  const renderMode = lockedMode ?? resolvedMode;

  React.useEffect(() => {
    if (open && lockedMode === null) {
      setLockedMode(resolvedMode);
    }
    if (!open && lockedMode !== null) {
      setLockedMode(null);
    }
  }, [lockedMode, open, resolvedMode]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setLockedMode(
        shouldRenderDrawer(mobileMode, isMobile) ? "drawer" : "dialog",
      );
    } else if (controlledOpen === undefined) {
      setLockedMode(null);
    }

    if (controlledOpen === undefined) {
      setUncontrolledOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
  };

  const contextValue = React.useMemo(() => ({ renderMode }), [renderMode]);

  if (renderMode === "drawer") {
    return (
      <DialogContext.Provider value={contextValue}>
        <Drawer
          data-slot="dialog"
          direction="bottom"
          modal={modal}
          onOpenChange={handleOpenChange}
          open={open}
        >
          {children}
        </Drawer>
      </DialogContext.Provider>
    );
  }

  return (
    <DialogContext.Provider value={contextValue}>
      <DialogPrimitive.Root
        data-slot="dialog"
        modal={modal}
        onOpenChange={handleOpenChange}
        open={open}
      >
        {children}
      </DialogPrimitive.Root>
    </DialogContext.Provider>
  );
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  const renderMode = useDialogRenderMode();

  if (renderMode === "drawer") {
    return <DrawerTrigger data-slot="dialog-trigger" {...props} />;
  }

  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  const renderMode = useDialogRenderMode();

  if (renderMode === "drawer") {
    return <DrawerClose data-slot="dialog-close" {...props} />;
  }

  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

const contentVariants = cva(
  "fixed inset-0 z-50 m-auto flex h-fit flex-col overflow-hidden rounded-lg border bg-background shadow-lg duration-200 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
  {
    variants: {
      size: {
        sm: "w-[400px] max-w-[calc(100svw-2rem)] min-h-[300px] max-h-[480px]",
        md: "w-[600px] max-w-[calc(100svw-2rem)] min-h-[440px] max-h-[640px]",
        lg: "w-[800px] max-w-[calc(100svw-2rem)] min-h-[560px] max-h-[720px]",
        full: "h-[calc(100svh-2rem)] w-[calc(100svw-2rem)]",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

type DialogContentProps = React.ComponentProps<typeof DialogPrimitive.Content> &
  VariantProps<typeof contentVariants> & {
    drawerClassName?: string;
    overlayClassName?: string;
    showCloseButton?: boolean;
  };

function DialogContent({
  children,
  className,
  drawerClassName,
  overlayClassName,
  onInteractOutside,
  ref,
  showCloseButton = true,
  size,
  ...props
}: DialogContentProps) {
  const renderMode = useDialogRenderMode();
  const [portalContainer, setPortalContainer] =
    React.useState<HTMLDivElement | null>(null);

  if (renderMode === "drawer") {
    return (
      <DrawerContent
        data-slot="dialog-content"
        ref={ref}
        className={drawerClassName}
        {...props}
      >
        <PortalContainerProvider value={portalContainer}>
          {children}
        </PortalContainerProvider>
        <div
          data-slot="dialog-portal-container"
          ref={setPortalContainer}
          className="pointer-events-none fixed inset-0 z-[70]"
        />
      </DrawerContent>
    );
  }

  return (
    <DialogPrimitive.Portal data-slot="dialog-portal">
      <DialogPrimitive.Overlay
        data-slot="dialog-overlay"
        className={cn(
          "fixed inset-0 z-50 bg-black/40 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
          overlayClassName,
        )}
      />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        ref={ref}
        className={cn(contentVariants({ size }), className)}
        onInteractOutside={(event) => {
          onInteractOutside?.(event);
          if (event.defaultPrevented) return;

          if (isComboboxPopupInteraction(event.target)) {
            event.preventDefault();
          }
        }}
        {...props}
      >
        <PortalContainerProvider value={portalContainer}>
          {children}
        </PortalContainerProvider>
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="absolute top-3 right-3 inline-flex size-4 items-center justify-center rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <Icon src="X" className="size-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
        <div
          data-slot="dialog-portal-container"
          ref={setPortalContainer}
          className="pointer-events-none fixed inset-0 z-[70]"
        />
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

function isComboboxPopupInteraction(target: EventTarget | null) {
  if (!(target instanceof Element)) return false;

  return target.closest(
    '[data-slot="combobox-content"], [data-slot="combobox-positioner"]',
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  const renderMode = useDialogRenderMode();

  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex shrink-0 flex-col gap-1.5 border-b bg-background p-4",
        renderMode === "drawer" &&
          "mx-auto w-full max-w-sm border-b-0 text-center md:text-left",
        className,
      )}
      {...props}
    />
  );
}

function DialogBody({ className, ...props }: React.ComponentProps<"div">) {
  const renderMode = useDialogRenderMode();

  return (
    <div
      data-slot="dialog-body"
      className={cn(
        "flex-1 overflow-y-auto p-4",
        renderMode === "drawer" && "mx-auto w-full max-w-sm px-4 py-2",
        className,
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  const renderMode = useDialogRenderMode();

  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex shrink-0 items-center justify-end gap-4 border-t bg-background p-4",
        renderMode === "drawer" &&
          "mx-auto mt-auto w-full max-w-sm flex-col items-stretch gap-2 border-t-0",
        className,
      )}
      {...props}
    />
  );
}

function DialogFooterLeading({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const renderMode = useDialogRenderMode();

  return (
    <div
      data-slot="dialog-footer-leading"
      className={cn(
        "mr-auto flex min-w-0 flex-1 items-center",
        renderMode === "drawer" && "mr-0 w-full flex-none",
        className,
      )}
      {...props}
    />
  );
}

function DialogFooterActions({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const renderMode = useDialogRenderMode();

  return (
    <div
      data-slot="dialog-footer-actions"
      className={cn(
        "flex shrink-0 items-center justify-end gap-2",
        renderMode === "drawer" && "w-full flex-col items-stretch",
        className,
      )}
      {...props}
    />
  );
}

type DialogFooterCheckboxProps = Omit<CheckboxProps, "children"> & {
  children: React.ReactNode;
};

function DialogFooterCheckbox({
  children,
  className,
  id,
  ...props
}: DialogFooterCheckboxProps) {
  const generatedId = React.useId();
  const checkboxId = id ?? generatedId;

  return (
    <Field orientation="horizontal" className="min-w-0">
      <Checkbox id={checkboxId} className={className} {...props} />
      <FieldContent className="min-w-0 gap-0">
        <FieldLabel
          htmlFor={checkboxId}
          className="truncate text-sm font-medium"
        >
          {children}
        </FieldLabel>
      </FieldContent>
    </Field>
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  const renderMode = useDialogRenderMode();

  if (renderMode === "drawer") {
    return (
      <DrawerTitle
        data-slot="dialog-title"
        className={cn("text-lg leading-none font-semibold", className)}
        {...props}
      />
    );
  }

  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  const renderMode = useDialogRenderMode();

  if (renderMode === "drawer") {
    return (
      <DrawerDescription
        data-slot="dialog-description"
        className={cn("text-sm text-muted-foreground", className)}
        {...props}
      />
    );
  }

  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogFooterLeading,
  DialogFooterActions,
  DialogFooterCheckbox,
  DialogTitle,
  DialogDescription,
};
export type { DialogContentProps, DialogMobileMode, DialogProps };
